/**
 * Lift2Fit — the legal route entry points.
 *
 * `/legal` is an index; `/legal/privacy`, `/legal/terms` and
 * `/legal/health-disclaimer` are the three documents the signup checkbox names.
 * All four render signed out, outside the app shell.
 */
import { LegalLayout, LegalP, LegalSection } from './LegalLayout';
import { PrivacyPolicy } from './PrivacyPolicy';
import { TermsOfService } from './TermsOfService';
import { HealthDisclaimer } from './HealthDisclaimer';
import { Link } from '../router';
import { LEGAL_DOCS, LEGAL_DOC_ORDER, POLICY_VERSION, effectiveDateLabel, type LegalDocId } from './policy';

export function LegalScreen({ docId }: { docId: LegalDocId }) {
  const meta = LEGAL_DOCS[docId];
  return (
    <LegalLayout title={meta.title} currentId={docId}>
      {docId === 'privacy_policy' ? (
        <PrivacyPolicy />
      ) : docId === 'terms_of_service' ? (
        <TermsOfService />
      ) : (
        <HealthDisclaimer />
      )}
    </LegalLayout>
  );
}

export function LegalIndex() {
  return (
    <LegalLayout title="Legal documents" currentId={null}>
      <LegalSection id="index" title="The documents">
        <LegalP>
          These are the three documents every Lift2Fit account accepts at signup. They are version{' '}
          {POLICY_VERSION}, effective {effectiveDateLabel()}, and they apply to {LEGAL_DOC_ORDER.length}{' '}
          documents taken together.
        </LegalP>
        <ul className="flex flex-col gap-3">
          {LEGAL_DOC_ORDER.map((id) => {
            const meta = LEGAL_DOCS[id];
            return (
              <li key={meta.id} className="border border-hairline bg-surface p-4">
                <Link
                  to={meta.path}
                  data-testid={`legal-index-${meta.id}`}
                  className="font-display text-[17px] font-black uppercase tracking-[-0.01em] text-ink hover:text-accent"
                >
                  {meta.title}
                </Link>
                <p className="mt-1 text-[14px] leading-relaxed text-ink-muted">{meta.summary}</p>
              </li>
            );
          })}
        </ul>
      </LegalSection>
      <LegalSection id="signup" title="Why you are asked to tick a box">
        <LegalP>
          Signup requires an explicit, unticked confirmation that you have read and understand all three
          documents. Lift2Fit records the version you accepted, with the time and the request’s User-Agent and IP
          address, so the acceptance is a fact rather than an assumption. You can see that record on your profile
          and in the Privacy Policy (sections 6 and 11).
        </LegalP>
        <LegalP>
          <Link to="/signup" className="font-bold text-accent hover:underline">
            Back to sign up
          </Link>
        </LegalP>
      </LegalSection>
    </LegalLayout>
  );
}
